import Constants from 'expo-constants';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

import { SECURE_STORE_KEYS } from '@/constants/api';
import { secureStore } from '@/lib/secureStore';
import { apiClient } from '@/services/api';

/**
 * Push registration + lifecycle. THE single place the app talks to
 * expo-notifications for tokens. Every exported function is wrapped so a
 * failure can NEVER crash the app or a caller — the worst outcome is "no token
 * registered", which just means the user gets no push. Backend contract:
 *   POST   /api/v1/device_tokens { token, platform }  → upsert (reassigns owner)
 *   DELETE /api/v1/device_tokens { token }            → sign-out cleanup
 *
 * iOS-only in v1 (Android channel is set defensively for when FCM lands). Web
 * short-circuits everywhere — push is a native feature here.
 */

const DEVICE_TOKENS_PATH = '/api/v1/device_tokens';

export type PushPermission = 'granted' | 'denied' | 'undetermined';

export type PushRegistration =
  | { status: 'granted'; token: string }
  | { status: 'denied' | 'undetermined'; token?: undefined };

function projectId(): string | undefined {
  // getExpoPushTokenAsync defaults to this same field; we pass it explicitly so
  // the token is unambiguously bound to our EAS project.
  const id = Constants.expoConfig?.extra?.eas?.projectId;
  return typeof id === 'string' ? id : undefined;
}

/** Current OS permission, without prompting. Never throws. */
export async function getPushPermission(): Promise<PushPermission> {
  if (Platform.OS === 'web') return 'undetermined';
  try {
    const { status } = await Notifications.getPermissionsAsync();
    return status as PushPermission;
  } catch (err) {
    if (__DEV__) console.warn('[push] getPermissions failed', err);
    return 'undetermined';
  }
}

/**
 * The idempotent registration entry point. Optionally prompts for permission,
 * then (on grant) fetches the Expo token and upserts it on the backend. Safe to
 * call from every entry point (priming, settings toggle, launch sync) — a
 * granted device just re-upserts the same token.
 */
export async function registerForPushNotificationsAsync(
  opts: { promptIfNeeded?: boolean } = {},
): Promise<PushRegistration> {
  const { promptIfNeeded = true } = opts;
  if (Platform.OS === 'web') return { status: 'undetermined' };

  try {
    let status = (await Notifications.getPermissionsAsync()).status as PushPermission;

    if (status === 'undetermined' && promptIfNeeded) {
      status = (await Notifications.requestPermissionsAsync()).status as PushPermission;
    }

    if (status !== 'granted') {
      return { status };
    }

    if (Platform.OS === 'android') {
      await ensureAndroidChannel();
    }

    const { data: token } = await Notifications.getExpoPushTokenAsync({
      projectId: projectId(),
    });

    await apiClient.post(DEVICE_TOKENS_PATH, {
      token,
      platform: Platform.OS,
    });

    return { status: 'granted', token };
  } catch (err) {
    // Simulator (no push hardware), no network, missing entitlement — all land
    // here. Never surface; the caller treats "no token" as "not enabled".
    if (__DEV__) console.warn('[push] registration failed', err);
    return { status: 'undetermined' };
  }
}

/**
 * Launch-time silent re-register: refreshes the token ONLY when permission is
 * already granted (never prompts). Heals reinstalls, token rotation, and a
 * re-login on a device that already had permission.
 */
export async function syncPushRegistration(): Promise<void> {
  const status = await getPushPermission();
  if (status !== 'granted') return;
  await registerForPushNotificationsAsync({ promptIfNeeded: false });
}

/**
 * Best-effort sign-out cleanup: drop THIS device's token server-side so the
 * signed-out user stops receiving pushes. Never throws — sign-out must proceed
 * regardless (token reassignment on the next user's registration and
 * DeviceNotRegistered pruning are the backstops).
 */
export async function unregisterPushNotificationsAsync(): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    if ((await getPushPermission()) !== 'granted') return;
    const { data: token } = await Notifications.getExpoPushTokenAsync({
      projectId: projectId(),
    });
    await apiClient.del(DEVICE_TOKENS_PATH, { token });
  } catch (err) {
    if (__DEV__) console.warn('[push] unregister failed', err);
  }
}

/** Whether we've already shown the one-time priming screen on this device. */
export async function hasPrimedPush(): Promise<boolean> {
  try {
    return (await secureStore.get(SECURE_STORE_KEYS.pushPrimed)) === '1';
  } catch {
    return false;
  }
}

/** Mark the priming screen as shown so we never nag again. */
export async function markPushPrimed(): Promise<void> {
  try {
    await secureStore.set(SECURE_STORE_KEYS.pushPrimed, '1');
  } catch (err) {
    if (__DEV__) console.warn('[push] failed to persist primed flag', err);
  }
}

async function ensureAndroidChannel(): Promise<void> {
  try {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.DEFAULT,
    });
  } catch (err) {
    if (__DEV__) console.warn('[push] android channel setup failed', err);
  }
}
